/**
 * The spell check's dictionary, a core setting of every app (I-3006): the
 * key `spell_check` of master's account config path, field
 * `personal_dictionary` — a map by word (`{ "Wort": true }`). A merge patch
 * adds a word with `true` and removes it with `null`, so two tabs adding
 * words at the same time never overwrite each other (a patch replaces an
 * array whole). It is a core key, not an app's: Write, Calc and Mail read the
 * same words, so a word added in one is known in all, and an app never
 * declares the key (Connect owns the declaration, see
 * `frontend/connect/src/settingsHub/connectSettingsSections.ts`).
 *
 * The layers merge to a UNION: master merges `spell_check` member by member
 * and a stored document never holds `null`, so the effective
 * `personal_dictionary` is every word a tenant, company, design, account or
 * profile layer adds. A word is removed on the layer that holds it.
 *
 * Apps use `useSpellDictionary()`: the words to check against, whether the
 * dictionary is locked, and `add` for „Zum Wörterbuch hinzufügen“. The
 * settings page („Wörterbuch“) and the admin app's company dictionary use the
 * helpers below on the raw handle (`useSpellCheckConfig`).
 *
 * Two more fields of the same key belong to the search (I-3024), both a map
 * by language (`de`/`en`) of a map by word: `corrections` (the wrong word →
 * `{ to, count, origin }`, one target per word and layer; `origin` own ·
 * learned · import, `count` 1–5 for a learned one, 0 otherwise) and
 * `never_suggest` (the word → `{ origin }`, own · import). Like the words,
 * the layers merge to their union and one entry is a merge patch; a tenant or
 * company locks each of the three fields on its own.
 */
import { computed } from "vue";
import type { ComputedRef } from "vue";
import {
  useAccountConfig,
  type AccountConfigHandle,
  type AccountConfigLayer,
  type AccountConfigTarget,
  type EnforcedField,
  type UseAccountConfigOptions,
} from "./accountConfig";
import {
  dictionaryEffectiveDocument,
  dictionaryEntryPatch,
  dictionaryFieldLocked,
  dictionaryLayers,
  dictionaryMapEntries,
  type DictionaryEntrySource,
} from "./dictionaryLayer";
import { accountConfigLayerSource, accountConfigLayerView } from "./layerView";
import { isJsonObject, type JsonObject } from "./mergePatch";
import {
  DICTIONARY_LANGUAGES,
  DICTIONARY_MAX_WORD,
  dictionaryAddition,
  dictionaryWord,
  sortDictionaryWords,
  type DictionaryLanguage,
} from "../text/dictionaryFile";

/** The core key the spell check's settings live in. */
export const SPELL_CHECK_CONFIG_KEY = "spell_check";

/** The field of the dictionary (a tenant's or company's lock of it freezes the words). */
export const SPELL_CHECK_DICTIONARY_FIELD = "personal_dictionary";

/** The field of the corrections (wrong word → right word, per language). */
export const SPELL_CHECK_CORRECTIONS_FIELD = "corrections";

/** The field of the words the search never suggests (per language). */
export const SPELL_CHECK_NEVER_SUGGEST_FIELD = "never_suggest";

/** Where a correction comes from: typed in (`own`), learned from accepted suggestions or imported. */
export type SpellCorrectionOrigin = "own" | "learned" | "import";

/** One correction: the word it stands for, how often it was learned (1–5, else 0) and where it comes from. */
export interface SpellCorrection {
  to: string;
  count: number;
  origin: SpellCorrectionOrigin;
}

/** Where a never-suggest word comes from. */
export type SpellNeverSuggestOrigin = "own" | "import";

export interface SpellNeverSuggest {
  origin: SpellNeverSuggestOrigin;
}

/** A language's map by word; `null` is a removal inside a merge patch. */
type ByLanguage<V> = Partial<Record<DictionaryLanguage, Record<string, V | null>>>;

/**
 * `spell_check` as stored: the words the spell check accepts on top of its
 * own, by word (`true`), and the search's corrections and never-suggest words.
 */
export interface SpellCheckConfig {
  personal_dictionary?: Record<string, boolean | null>;
  corrections?: ByLanguage<SpellCorrection>;
  never_suggest?: ByLanguage<SpellNeverSuggest>;
}

/** How adding one word ended: saved, already there, no word the dictionary takes, locked, or the write failed. */
export type SpellDictionaryAddResult =
  | "added"
  | "known"
  | "invalid"
  | "locked"
  | "failed";

export interface SpellDictionaryHandle {
  /** Effective words (union of every layer: tenant, company, design, account, profile), sorted for a German reader. */
  words: ComputedRef<string[]>;
  /** A tenant or company locked the dictionary. */
  locked: ComputedRef<boolean>;
  /** Adds one word on the session's own layer and resolves once master stored it. */
  add(word: string): Promise<SpellDictionaryAddResult>;
}

/** The longest word the dictionary takes (the shared word rule, `text/dictionaryFile.ts`). */
export const SPELL_DICTIONARY_MAX_WORD = DICTIONARY_MAX_WORD;

/** A word as the dictionary keeps it: trimmed; undefined when there is none or it is too long. */
export const spellDictionaryWord: (word: string) => string | undefined =
  dictionaryWord;

/** `spell_check` of the session; `layers` reads the stored layers too (the settings page needs them). */
export function useSpellCheckConfig(
  options: Pick<UseAccountConfigOptions<SpellCheckConfig>, "layers"> = {},
): AccountConfigHandle<SpellCheckConfig> {
  return useAccountConfig<SpellCheckConfig>(SPELL_CHECK_CONFIG_KEY, options);
}

/** The effective words, sorted the way a German reader expects. */
export function spellDictionaryWords(
  config: SpellCheckConfig | undefined,
): string[] {
  const words = Object.entries(config?.personal_dictionary ?? {})
    .filter(([, on]) => on === true)
    .map(([word]) => word);
  return sortDictionaryWords(words);
}

/** Whether a tenant or company locked the dictionary. */
export function spellDictionaryLocked(
  enforced: readonly EnforcedField[],
): boolean {
  return enforced.some((entry) => entry.field === SPELL_CHECK_DICTIONARY_FIELD);
}

/**
 * The merge patch that brings the dictionary from `current` to `next` — a
 * word added is `true`, a word removed `null`. Undefined when nothing changes.
 */
export function spellDictionaryPatch(
  current: readonly string[],
  next: readonly string[],
): SpellCheckConfig | undefined {
  const before = new Set(current);
  const after = new Set(
    next
      .map(spellDictionaryWord)
      .filter((word): word is string => !!word),
  );
  const change: Record<string, boolean | null> = {};
  for (const word of after) if (!before.has(word)) change[word] = true;
  for (const word of before) if (!after.has(word)) change[word] = null;
  return Object.keys(change).length
    ? { personal_dictionary: change }
    : undefined;
}

/** The merge patch that removes one word from the layer that is written. */
export function spellDictionaryRemoval(word: string): SpellCheckConfig {
  return { personal_dictionary: { [word]: null } };
}

/** What adding several words at once (the import) amounts to. */
export interface SpellDictionaryAddition {
  /** ONE merge patch with every new word (`true`); undefined when none is new. */
  patch: SpellCheckConfig | undefined;
  /** The words the patch adds. */
  added: string[];
  /** Words left out: already in the dictionary, given twice or no word the dictionary takes. */
  skipped: number;
}

/** Adds `candidates` to a dictionary that holds `existing` (the effective words): one patch for all. */
export function spellDictionaryAddition(
  existing: Iterable<string>,
  candidates: readonly string[],
): SpellDictionaryAddition {
  const { change, added, skipped } = dictionaryAddition(existing, candidates);
  return {
    patch: added.length ? { personal_dictionary: change } : undefined,
    added,
    skipped,
  };
}

/** Where a word of the effective dictionary comes from. */
export type SpellDictionarySource = DictionaryEntrySource;

export interface SpellDictionaryEntry {
  word: string;
  source: SpellDictionarySource;
}

function sourceOf(subjectType: string | undefined): SpellDictionarySource {
  if (subjectType === "company") return "company";
  if (subjectType === "tenant") return "tenant";
  return "inherited";
}

/**
 * The effective dictionary's words, sorted for a German reader, each with its
 * origin: `own` when the session's layer (`target`) holds it, else the layer
 * before it that brings it. A word both hold counts as inherited — removing
 * the own copy would change nothing. Without the layers every word is `own`.
 */
export function spellDictionaryEntries(
  config: SpellCheckConfig | undefined,
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
): SpellDictionaryEntry[] {
  const words = spellDictionaryWords(
    dictionaryEffectiveDocument(config, layers) as SpellCheckConfig | undefined,
  );
  if (!layers || !target) return words.map((word) => ({ word, source: "own" }));
  const view = accountConfigLayerView(dictionaryLayers(layers), {
    subjectType: target.subjectType,
    subjectId: target.subjectId,
  });
  const inherited = view.inherited[SPELL_CHECK_DICTIONARY_FIELD];
  const before = isJsonObject(inherited) ? inherited : {};
  return words.map((word) => {
    if (before[word] !== true) return { word, source: "own" };
    const layer = accountConfigLayerSource(view, [
      SPELL_CHECK_DICTIONARY_FIELD,
      word,
    ]);
    return { word, source: sourceOf(layer?.subjectType) };
  });
}

/**
 * Adds one word (the editor's „Zum Wörterbuch hinzufügen“) to the session's
 * layer and waits for master's answer, so an app only treats the word as
 * known once it is stored.
 */
export async function addSpellDictionaryWord(
  handle: AccountConfigHandle<SpellCheckConfig>,
  word: string,
): Promise<SpellDictionaryAddResult> {
  const current = spellDictionaryWords(handle.effective.value);
  const kept = spellDictionaryWord(word);
  if (!kept) return "invalid";
  if (current.includes(kept)) return "known";
  if (spellDictionaryLocked(handle.enforced.value)) return "locked";
  const result = await handle.patch(
    spellDictionaryPatch(current, [...current, kept])!,
  );
  return result === null ? "failed" : "added";
}

/**
 * The dictionary of the session for an app's spell check: the words to check
 * against, whether it is locked, and `add`. Needs no declaration — the key is
 * a core key, read straight from the account config path.
 */
export function useSpellDictionary(): SpellDictionaryHandle {
  const handle = useSpellCheckConfig();
  return {
    words: computed(() => spellDictionaryWords(handle.effective.value)),
    locked: computed(() => spellDictionaryLocked(handle.enforced.value)),
    add: (word) => addSpellDictionaryWord(handle, word),
  };
}

/* ── Corrections (I-3024) ────────────────────────────────────────────────── */

/** The most a learned correction counts (a saturating counter). */
export const SPELL_CORRECTION_LEARN_MAX = 5;

/** One row of the corrections list: a wrong word, its target, origin and layer. */
export interface SpellCorrectionEntry extends SpellCorrection {
  lang: DictionaryLanguage;
  typed: string;
  source: SpellDictionarySource;
}

/** A stored correction as the helpers read it; undefined for a value that is no correction. */
export function readSpellCorrection(value: unknown): SpellCorrection | undefined {
  if (!isJsonObject(value) || typeof value.to !== "string" || !value.to) return undefined;
  const origin = value.origin;
  return {
    to: value.to,
    count: typeof value.count === "number" ? value.count : 0,
    origin: origin === "learned" || origin === "import" ? origin : "own",
  };
}

/** Whether a tenant or company locked the corrections. */
export function spellCorrectionsLocked(enforced: readonly EnforcedField[]): boolean {
  return dictionaryFieldLocked(enforced, SPELL_CHECK_CORRECTIONS_FIELD);
}

/**
 * The effective corrections by language and wrong word, each with its origin
 * and the layer it comes from (`own` where the session's layer holds it;
 * without the layers every row is own).
 */
export function spellCorrectionEntries(
  config: SpellCheckConfig | undefined,
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
): SpellCorrectionEntry[] {
  return dictionaryMapEntries(
    config,
    SPELL_CHECK_CORRECTIONS_FIELD,
    layers,
    target,
    readSpellCorrection,
  ).map((entry) => ({
    lang: entry.lang,
    typed: entry.key,
    ...entry.value,
    source: entry.source,
  }));
}

/**
 * The merge patch that writes one correction; `renamedFrom` is the wrong word
 * it had before (that key goes to `null` in the same patch).
 */
export function spellCorrectionPatch(
  lang: DictionaryLanguage,
  typed: string,
  value: SpellCorrection,
  renamedFrom?: string,
): SpellCheckConfig {
  return dictionaryEntryPatch(
    SPELL_CHECK_CORRECTIONS_FIELD,
    lang,
    typed,
    value,
    renamedFrom,
  ) as SpellCheckConfig;
}

/** The merge patch that removes one correction from the layer that is written. */
export function spellCorrectionRemoval(
  lang: DictionaryLanguage,
  typed: string,
): SpellCheckConfig {
  return dictionaryEntryPatch(
    SPELL_CHECK_CORRECTIONS_FIELD,
    lang,
    typed,
    null,
  ) as SpellCheckConfig;
}

/* ── Never suggest (I-3024) ──────────────────────────────────────────────── */

/** One row of the never-suggest list. */
export interface SpellNeverSuggestEntry {
  lang: DictionaryLanguage;
  word: string;
  origin: SpellNeverSuggestOrigin;
  source: SpellDictionarySource;
}

function readNeverSuggest(value: unknown): SpellNeverSuggest | undefined {
  if (!isJsonObject(value)) return undefined;
  return { origin: value.origin === "import" ? "import" : "own" };
}

/** Whether a tenant or company locked the never-suggest words. */
export function spellNeverSuggestLocked(enforced: readonly EnforcedField[]): boolean {
  return dictionaryFieldLocked(enforced, SPELL_CHECK_NEVER_SUGGEST_FIELD);
}

/** The effective never-suggest words by language, each with its origin and layer. */
export function spellNeverSuggestEntries(
  config: SpellCheckConfig | undefined,
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
): SpellNeverSuggestEntry[] {
  return dictionaryMapEntries(
    config,
    SPELL_CHECK_NEVER_SUGGEST_FIELD,
    layers,
    target,
    readNeverSuggest,
  ).map((entry) => ({
    lang: entry.lang,
    word: entry.key,
    origin: entry.value.origin,
    source: entry.source,
  }));
}

/** The effective never-suggest words of every language (what the search filters on). */
export function spellNeverSuggestWords(config: SpellCheckConfig | undefined): string[] {
  return spellNeverSuggestEntries(config, null, null).map((entry) => entry.word);
}

/**
 * The merge patch of a „nicht mehr vorschlagen“ click: the word with origin
 * `own`. Undefined when `word` is no word the dictionary takes.
 */
export function spellNeverSuggestPatch(
  lang: DictionaryLanguage,
  word: string,
): SpellCheckConfig | undefined {
  const kept = dictionaryWord(word);
  if (!kept) return undefined;
  return dictionaryEntryPatch(
    SPELL_CHECK_NEVER_SUGGEST_FIELD,
    lang,
    kept,
    { origin: "own" satisfies SpellNeverSuggestOrigin },
  ) as SpellCheckConfig;
}

/** The merge patch that allows a word again (removes it from the layer that is written). */
export function spellNeverSuggestRemoval(
  lang: DictionaryLanguage,
  word: string,
): SpellCheckConfig {
  return dictionaryEntryPatch(
    SPELL_CHECK_NEVER_SUGGEST_FIELD,
    lang,
    word,
    null,
  ) as SpellCheckConfig;
}

/**
 * ONE merge patch that allows every own word of the list again („Alle wieder
 * zulassen“), of one language or — without `lang` — of both. Undefined when
 * the own layer holds none.
 */
export function spellNeverSuggestAllow(
  entries: readonly SpellNeverSuggestEntry[],
  lang?: DictionaryLanguage,
): SpellCheckConfig | undefined {
  const field: Record<string, Record<string, null>> = {};
  for (const entry of entries) {
    if (entry.source !== "own" || (lang && entry.lang !== lang)) continue;
    (field[entry.lang] ??= {})[entry.word] = null;
  }
  return Object.keys(field).length
    ? ({ [SPELL_CHECK_NEVER_SUGGEST_FIELD]: field } as SpellCheckConfig)
    : undefined;
}

/** The languages the dictionary is kept in. */
export const SPELL_CHECK_LANGUAGES = DICTIONARY_LANGUAGES;
